// Collage engine — the SCOPED EFFECT STACK (Order 158.061, canon
// collage_type_system §3, names LOCKED by Matt 2026-07-18).
//
// The model, in Matt's After Effects terms: a composition takes effects with
// parameters; compositions nest inside compositions; objects may carry their
// own frame loop plus effect animations keyframed across those frames. This
// module is the one home for that model's DATA: 15 named effects in 5 stack
// groups, each declaring the scopes it is addressable at and the dials it
// exposes.
//
//   C — composition          (the recipe/scene)
//   N — nested composition   (a window layer's interior stack)
//   O — object               (a recipe layer / emitted piece)
//   F — individual frame     (a param keyframed across an object's own frames)
//
// WHAT THIS REFACTOR DOES NOT DO. The Order 158.4 scene fields (frame_cadence,
// boil, camera, print{grain, halftone, misregister_px, dust}) become PRESETS
// inside this model — a preset-backed effect reads its values from the same
// resolution path that shipped in 158.4, so skins keep working and existing
// recipes render byte-compatible (tests/collage-golden.test.ts is the gate).
// The FX stack is the ADDRESSING and DIAL surface over that resolution, not a
// second compute path: adding a parallel evaluator for effects that already
// render correctly would be a drift bug with no upside.
//
// NO AESTHETIC RULING WAS MADE IN THIS ORDER. Every dial introduced here ships
// `provisional: true` and defaults to a NO-OP, so a fresh recipe renders
// exactly as it did before the dial existed. Values become law only when Matt
// dials and locks (canon §6); until then a provisional default is a starting
// point, not a house value.
//
// Dependency-free and host-neutral, like the rest of anim-schema.

import type { CollageSkin } from './collage-skins';
import type { CollageRecipe, RecipeLayer } from './collage-recipe';

// --- Scopes ---------------------------------------------------------------------

export const FX_SCOPES = ['C', 'N', 'O', 'F'] as const;
export type FxScope = (typeof FX_SCOPES)[number];

export const FX_SCOPE_NAMES: Record<FxScope, string> = {
  C: 'composition',
  N: 'nested composition',
  O: 'object',
  F: 'frame',
};

// Inheritance chain, broadest → narrowest. A narrower scope's explicit params
// win; anything it leaves unset falls through to the scope above it. This is
// what makes "keyframe one dial on frame 4" a local edit instead of a respec.
export const FX_SCOPE_CHAIN: readonly FxScope[] = ['C', 'N', 'O', 'F'];

// --- Stack groups (render order: group I first → group V last) ---------------------
// Canon §3 lists the stack top group → bottom group; `order` is the render
// sequence, and the registry below is declared in that order.

export const FX_GROUPS = [
  'source-treatment', 'edge-surface', 'print-pass', 'temporal', 'camera',
] as const;
export type FxGroup = (typeof FX_GROUPS)[number];

export const FX_GROUP_LABELS: Record<FxGroup, string> = {
  'source-treatment': 'I · SOURCE TREATMENT',
  'edge-surface': 'II · EDGE & SURFACE',
  'print-pass': 'III · PRINT PASS',
  temporal: 'IV · TEMPORAL',
  camera: 'V · CAMERA',
};

// --- Dials -------------------------------------------------------------------------

export type FxValue = number | boolean | string | null;

export type FxDial =
  | { key: string; kind: 'number'; min: number; max: number; default: number; step?: number; provisional: boolean; note?: string }
  | { key: string; kind: 'bool'; default: boolean; provisional: boolean; note?: string }
  | { key: string; kind: 'enum'; options: readonly string[]; default: string; provisional: boolean; note?: string }
  // An ink reference: a role, a skin ink name, or raw hex (resolveInk's grammar).
  | { key: string; kind: 'ink'; default: string | null; provisional: boolean; note?: string }
  | { key: string; kind: 'seed'; default: number | null; provisional: boolean; note?: string };

export interface FxSpec {
  code: string;              // FX-1 … FX-15 (canon codes, LOCKED)
  id: string;                // stable slug used in recipes + routes
  name: string;              // canon display name, LOCKED
  group: FxGroup;
  order: number;             // render order within the whole stack
  scopes: readonly FxScope[];
  dials: readonly FxDial[];
  // 'preset'  — fully backed by a shipped 158.4 field; values flow from the
  //             existing resolution path (byte-compatible).
  // 'partial' — partly shipped; the new dials on it are provisional.
  // 'new'     — introduced by this Order; no-op at defaults.
  status: 'preset' | 'partial' | 'new';
  // Which shipped field this effect presets, when it presets one. Read by the
  // preset projection below and by the dial surface (158.066) for labelling.
  presets?: string;
  note?: string;
}

const n = (
  key: string, min: number, max: number, def: number, provisional: boolean, note?: string,
): FxDial => ({ key, kind: 'number', min, max, default: def, provisional, note });

// --- The registry (15, canon §3 — names and scopes are LAW) -------------------------

export const COLLAGE_FX: readonly FxSpec[] = [
  // I · SOURCE TREATMENT
  {
    code: 'FX-1', id: 'threshold-invert', name: 'Threshold / Invert',
    group: 'source-treatment', order: 1, scopes: ['O', 'F'], status: 'new',
    note: 'OB-2 Print Fragment territory: engraving-era sources run high-contrast, optionally white-on-black.',
    dials: [
      // threshold null/0 = off (source passes through untouched).
      n('threshold', 0, 1, 0, true, '0 = no thresholding'),
      { key: 'invert', kind: 'bool', default: false, provisional: true },
    ],
  },
  {
    code: 'FX-2', id: 'palette-map', name: 'Palette Map',
    group: 'source-treatment', order: 2, scopes: ['C', 'N', 'O', 'F'], status: 'new',
    note: 'Duotone at the object scope is the shipped texture.duotone; palette/fade are new.',
    dials: [
      // A skin palette name, or null for the source's own colour.
      { key: 'palette', kind: 'enum', options: ['none', 'skin', 'duotone'], default: 'none', provisional: true },
      n('fade', 0, 1, 0, true, '0 = full strength'),
      { key: 'duotone_ink', kind: 'ink', default: null, provisional: true },
      { key: 'duotone_paper', kind: 'ink', default: null, provisional: true },
    ],
  },
  {
    code: 'FX-3', id: 'halftone', name: 'Halftone',
    group: 'source-treatment', order: 3, scopes: ['C', 'O', 'F'], status: 'preset',
    presets: 'print.halftone / texture.halftone_dot_px',
    dials: [
      n('dot_px', 0, 64, 0, false, 'preset — skin print.halftone.dot_px'),
      n('opacity', 0, 1, 0, false, 'preset — skin print.halftone.opacity'),
      // Screen angle is the one genuinely new dial on a shipped effect.
      n('angle', 0, 180, 0, true, 'provisional — no screen rotation shipped in 158.4'),
    ],
  },

  // II · EDGE & SURFACE
  {
    code: 'FX-4', id: 'edge', name: 'Edge',
    group: 'edge-surface', order: 4, scopes: ['O'], status: 'partial',
    presets: 'layer.edge',
    dials: [
      { key: 'family', kind: 'enum', options: ['none', 'torn', 'cut', 'punched'], default: 'none', provisional: false },
      n('roughness', 0, 1, 0.5, true, 'provisional beyond the shipped torn roughness'),
      // The white rim on a photo cutout (OB-1). Default 0 per canon.
      n('rim_px', 0, 24, 0, true, 'provisional — canon default 0'),
    ],
  },
  {
    code: 'FX-5', id: 'paper-stock', name: 'Paper Stock',
    group: 'edge-surface', order: 5, scopes: ['C', 'N', 'O'], status: 'partial',
    presets: 'skin.paper_hex',
    dials: [
      { key: 'stock', kind: 'enum', options: ['none', 'smooth', 'laid', 'newsprint', 'kraft'], default: 'none', provisional: true },
      n('crumple', 0, 1, 0, true, 'provisional — 0 = flat'),
    ],
  },
  {
    code: 'FX-6', id: 'paint-over', name: 'Paint-Over',
    group: 'edge-surface', order: 6, scopes: ['O', 'F'], status: 'new',
    note: "Matt's painted-pass example: a brush pass keyframed frame-by-frame on top of a photo-sequence loop.",
    dials: [
      { key: 'brush', kind: 'enum', options: ['none', 'dry', 'gouache', 'ink'], default: 'none', provisional: true },
      n('coverage', 0, 1, 0, true, '0 = no paint pass'),
      { key: 'ink', kind: 'ink', default: null, provisional: true },
    ],
  },

  // III · PRINT PASS
  {
    code: 'FX-7', id: 'misregistration', name: 'Misregistration',
    group: 'print-pass', order: 7, scopes: ['C', 'O'], status: 'preset',
    presets: 'print.misregister_px',
    dials: [
      n('offset_px', 0, 3, 0, false, 'preset — skin print.misregister_px'),
      { key: 'channels', kind: 'enum', options: ['rgb', 'ink-paper'], default: 'rgb', provisional: true },
    ],
  },
  {
    code: 'FX-8', id: 'grain', name: 'Grain',
    group: 'print-pass', order: 8, scopes: ['C', 'N', 'O'], status: 'preset',
    presets: 'print.grain / texture.grain',
    dials: [n('amount', 0, 1, 0, false, 'preset — skin print.grain')],
  },
  {
    code: 'FX-9', id: 'dust', name: 'Dust',
    group: 'print-pass', order: 9, scopes: ['C', 'N', 'O'], status: 'preset',
    presets: 'print.dust',
    dials: [
      n('density', 0, 1, 0, false, 'preset — skin print.dust.density'),
      n('opacity', 0, 1, 0, false, 'preset — skin print.dust.opacity'),
      n('size', 0, 4, 1, true, 'provisional — 1 = the shipped speck scale'),
      { key: 'seed', kind: 'seed', default: null, provisional: false, note: 'null = derived from the recipe seed' },
    ],
  },
  {
    code: 'FX-10', id: 'ink-blot', name: 'Ink Blot',
    group: 'print-pass', order: 10, scopes: ['C', 'O', 'F'], status: 'new',
    dials: [
      n('density', 0, 1, 0, true, '0 = no blots'),
      n('size', 0, 1, 0.3, true),
      { key: 'color', kind: 'ink', default: null, provisional: true },
      { key: 'seed', kind: 'seed', default: null, provisional: false, note: 'null = derived from the recipe seed' },
    ],
  },

  // IV · TEMPORAL
  {
    code: 'FX-11', id: 'frame-cadence', name: 'Frame Cadence',
    group: 'temporal', order: 11, scopes: ['C', 'N', 'O'], status: 'preset',
    presets: 'frame_cadence',
    dials: [n('fps', 0, 60, 0, false, 'preset — stepped clock; 12 = "on twos". 0 = off')],
  },
  {
    code: 'FX-12', id: 'boil', name: 'Boil',
    group: 'temporal', order: 12, scopes: ['C', 'O', 'F'], status: 'preset',
    presets: 'boil',
    dials: [
      n('seeds', 2, 3, 3, false, 'preset — boil.seeds'),
      n('amount', 0, 1, 0, false, 'preset — boil.amount'),
    ],
  },
  {
    code: 'FX-13', id: 'flicker-swap', name: 'Flicker Swap',
    group: 'temporal', order: 13, scopes: ['O', 'F'], status: 'new',
    note: 'The eye↔butterfly corpus GIF: an object swaps between sources on a rate.',
    dials: [
      n('rate', 0, 24, 0, true, '0 = no swapping'),
      // Source set is authored as a slug list elsewhere; the dial names which
      // set to cycle, keeping this module dependency-free.
      { key: 'source_set', kind: 'enum', options: ['none', 'layer-sources'], default: 'none', provisional: true },
    ],
  },
  {
    code: 'FX-14', id: 'ghost-echo', name: 'Ghost Echo',
    group: 'temporal', order: 14, scopes: ['O'], status: 'new',
    dials: [
      n('opacity', 0, 1, 0, true, '0 = no echo'),
      n('offset', 0, 1, 0.02, true, 'fraction of canvas width per echo'),
      n('count', 0, 6, 0, true, '0 = no echo'),
    ],
  },

  // V · CAMERA
  {
    code: 'FX-15', id: 'camera', name: 'Camera',
    group: 'camera', order: 15, scopes: ['C', 'N'], status: 'preset',
    presets: 'camera',
    dials: [
      { key: 'verb', kind: 'enum', options: ['none', 'push', 'pull', 'pan'], default: 'none', provisional: false },
      n('amount', 0, 1, 0.5, false, 'preset — camera.amount'),
      n('duration', 0, 60, 0, false, 'preset — 0 = the whole scene duration'),
      n('defocus', 0, 1, 0, false, 'preset — camera.defocus'),
    ],
  },
];

if (COLLAGE_FX.length !== 15) {
  throw new Error(`collage-fx: canon §3 locks 15 effects, registry has ${COLLAGE_FX.length}`);
}

export const COLLAGE_FX_BY_ID: Record<string, FxSpec> =
  Object.fromEntries(COLLAGE_FX.map((f) => [f.id, f]));
export const COLLAGE_FX_BY_CODE: Record<string, FxSpec> =
  Object.fromEntries(COLLAGE_FX.map((f) => [f.code, f]));

// Registry integrity — fail at module load, not mid-compose.
{
  const codes = new Set<string>();
  const ids = new Set<string>();
  COLLAGE_FX.forEach((f, i) => {
    if (codes.has(f.code)) throw new Error(`collage-fx: duplicate code ${f.code}`);
    if (ids.has(f.id)) throw new Error(`collage-fx: duplicate id ${f.id}`);
    codes.add(f.code);
    ids.add(f.id);
    if (f.order !== i + 1) throw new Error(`collage-fx: ${f.code} order ${f.order} breaks stack sequence (expected ${i + 1})`);
    if (f.scopes.length === 0) throw new Error(`collage-fx: ${f.code} declares no scopes`);
    const seen = new Set<string>();
    for (const d of f.dials) {
      if (seen.has(d.key)) throw new Error(`collage-fx: ${f.code} duplicate dial "${d.key}"`);
      seen.add(d.key);
    }
  });
}

export function fxSpec(idOrCode: string): FxSpec {
  const spec = COLLAGE_FX_BY_ID[idOrCode] ?? COLLAGE_FX_BY_CODE[idOrCode];
  if (!spec) {
    throw new Error(
      `unknown collage effect "${idOrCode}" — known: ${COLLAGE_FX.map((f) => `${f.code}/${f.id}`).join(', ')}`,
    );
  }
  return spec;
}

export function fxSupportsScope(idOrCode: string, scope: FxScope): boolean {
  return fxSpec(idOrCode).scopes.includes(scope);
}

// Every effect addressable at a given scope, in stack order.
export function fxAtScope(scope: FxScope): FxSpec[] {
  return COLLAGE_FX.filter((f) => f.scopes.includes(scope));
}

// --- Params ------------------------------------------------------------------------

export type FxParams = Record<string, FxValue>;

export function fxDefaults(idOrCode: string): FxParams {
  const spec = fxSpec(idOrCode);
  const out: FxParams = {};
  for (const d of spec.dials) out[d.key] = d.default;
  return out;
}

// Validate + coerce one dial value. Throws with the dial path named, matching
// the recipe validator's throw-no-silent-fallback convention.
export function validateFxParam(idOrCode: string, key: string, value: unknown): FxValue {
  const spec = fxSpec(idOrCode);
  const dial = spec.dials.find((d) => d.key === key);
  if (!dial) {
    throw new Error(
      `collage-fx: ${spec.code} has no dial "${key}" — dials: ${spec.dials.map((d) => d.key).join(', ')}`,
    );
  }
  switch (dial.kind) {
    case 'number': {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw new Error(`collage-fx: ${spec.code}.${key} must be a number`);
      }
      if (value < dial.min || value > dial.max) {
        throw new Error(`collage-fx: ${spec.code}.${key} must be between ${dial.min} and ${dial.max}`);
      }
      return value;
    }
    case 'bool':
      if (typeof value !== 'boolean') throw new Error(`collage-fx: ${spec.code}.${key} must be a boolean`);
      return value;
    case 'enum':
      if (typeof value !== 'string' || !dial.options.includes(value)) {
        throw new Error(`collage-fx: ${spec.code}.${key} must be one of: ${dial.options.join(', ')}`);
      }
      return value;
    case 'ink':
      if (value === null) return null;
      if (typeof value !== 'string' || value.length === 0) {
        throw new Error(`collage-fx: ${spec.code}.${key} must be an ink reference or null`);
      }
      return value;
    case 'seed':
      if (value === null) return null;
      if (typeof value !== 'number' || !Number.isInteger(value)) {
        throw new Error(`collage-fx: ${spec.code}.${key} must be an integer seed or null`);
      }
      return value;
  }
}

export function validateFxParams(idOrCode: string, params: Record<string, unknown>): FxParams {
  const out: FxParams = {};
  for (const [k, v] of Object.entries(params)) out[k] = validateFxParam(idOrCode, k, v);
  return out;
}

// One effect's settings at one scope. `enabled: false` switches the effect off
// at this scope even when a broader scope turned it on (the explicit-null
// semantics the 158.4 scene fields already use).
export interface FxSetting {
  enabled?: boolean;
  params?: FxParams;
}

export type FxSettingMap = Record<string, FxSetting>;

// A scope's stack: which effects are set here, and how.
export interface FxStackEntry {
  spec: FxSpec;
  enabled: boolean;
  params: FxParams;
  // True when every param is still at its registry default AND the effect is
  // not preset-backed — i.e. this entry renders as a no-op. The dial surface
  // greys these; the renderer skips them.
  noop: boolean;
  // Whether any param carrying a value is still marked provisional. Canon: no
  // aesthetic ruling was made, so provisional values must stay visible as such.
  provisional: boolean;
}

function isDefault(spec: FxSpec, params: FxParams): boolean {
  return spec.dials.every((d) => {
    const v = params[d.key];
    return v === undefined || v === d.default;
  });
}

function carriesProvisional(spec: FxSpec, params: FxParams): boolean {
  return spec.dials.some((d) => {
    const v = params[d.key];
    return d.provisional && v !== undefined && v !== d.default;
  });
}

export function fxStackEntry(idOrCode: string, setting: FxSetting | undefined): FxStackEntry {
  const spec = fxSpec(idOrCode);
  const params = { ...fxDefaults(spec.id), ...(setting?.params ?? {}) };
  return {
    spec,
    enabled: setting?.enabled ?? true,
    params,
    noop: spec.status === 'new' && isDefault(spec, params),
    provisional: carriesProvisional(spec, params),
  };
}

// --- Preset projection (the "158.4 fields become presets" half) ----------------------
// Reads the SHIPPED resolution — recipe over skin, exactly as resolveCollageScene
// and the print-pass resolution already do it — and projects it into C-scope FX
// settings. This is a VIEW: the renderer still consumes the resolved scene and
// print pass, so nothing here can move an existing recipe's output. What it buys
// is that the dial surface (158.066) shows one uniform stack whether a value
// arrived from a skin default, a 158.4 recipe field, or an FX-scoped override.

export function presetFxSettings(recipe: CollageRecipe, skin: CollageSkin): FxSettingMap {
  const out: FxSettingMap = {};

  // FX-11 Frame Cadence ← frame_cadence (recipe over skin; absent everywhere = off)
  const cadence = recipe.frame_cadence ?? skin.frame_cadence ?? null;
  out['frame-cadence'] = { enabled: cadence !== null, params: { fps: cadence ?? 0 } };

  // FX-12 Boil ← boil. Explicit recipe null = OFF even under a house skin.
  const boil = recipe.boil === null
    ? null
    : recipe.boil
      ? { seeds: recipe.boil.seeds ?? 3, amount: recipe.boil.amount ?? 0.35 }
      : skin.boil ?? null;
  out['boil'] = {
    enabled: boil !== null,
    params: { seeds: boil?.seeds ?? 3, amount: boil?.amount ?? 0 },
  };

  // FX-15 Camera ← camera (recipe-only; a skin carries no camera default).
  const cam = recipe.camera ?? null;
  out['camera'] = {
    enabled: cam !== null,
    params: {
      verb: cam?.move ?? 'none',
      amount: cam?.amount ?? 0.5,
      duration: 0,
      defocus: cam?.defocus ?? 0,
    },
  };

  // Print pass ← recipe.print over skin.print. recipe.print === null = the whole
  // pass off, which is why each entry checks it before falling through.
  const printOff = recipe.print === null;
  const grain = printOff ? 0 : recipe.print?.grain ?? skin.print.grain;
  out['grain'] = { enabled: !printOff && grain > 0, params: { amount: grain } };

  const halftone = printOff
    ? null
    : recipe.print?.halftone === null ? null : recipe.print?.halftone ?? skin.print.halftone;
  out['halftone'] = {
    enabled: halftone !== null,
    params: { dot_px: halftone?.dot_px ?? 0, opacity: halftone?.opacity ?? 0, angle: 0 },
  };

  const mis = printOff ? 0 : recipe.print?.misregister_px ?? skin.print.misregister_px;
  out['misregistration'] = { enabled: !printOff && mis > 0, params: { offset_px: mis, channels: 'rgb' } };

  const dust = printOff
    ? null
    : recipe.print?.dust === null ? null : recipe.print?.dust ?? skin.print.dust ?? null;
  out['dust'] = {
    enabled: dust !== null,
    params: { density: dust?.density ?? 0, opacity: dust?.opacity ?? 0, size: 1, seed: null },
  };

  return out;
}

// The O-scope presets a layer already carries in shipped grammar: its edge
// family, and its per-layer texture (grain / halftone / duotone).
export function presetLayerFxSettings(layer: RecipeLayer): FxSettingMap {
  const out: FxSettingMap = {};
  if (layer.edge) {
    const roughness = layer.edge.family === 'torn' ? layer.edge.roughness : 0.5;
    out['edge'] = { enabled: layer.edge.family !== 'none', params: { family: layer.edge.family, roughness, rim_px: 0 } };
  }
  const t = layer.texture;
  if (t) {
    if (t.grain !== undefined) out['grain'] = { enabled: t.grain > 0, params: { amount: t.grain } };
    if (t.halftone_dot_px !== undefined) {
      out['halftone'] = { enabled: t.halftone_dot_px > 0, params: { dot_px: t.halftone_dot_px, opacity: 1, angle: 0 } };
    }
    if (t.duotone) {
      out['palette-map'] = {
        enabled: true,
        params: { palette: 'duotone', fade: 0, duotone_ink: t.duotone.ink, duotone_paper: t.duotone.paper },
      };
    }
  }
  return out;
}

// --- Scoped addressing --------------------------------------------------------------

export type FxTarget =
  | { scope: 'C' }
  | { scope: 'N'; layer: string }
  | { scope: 'O'; layer: string }
  | { scope: 'F'; layer: string; frame: number };

export function fxTargetKey(target: FxTarget): string {
  switch (target.scope) {
    case 'C': return 'C';
    case 'N': return `N:${target.layer}`;
    case 'O': return `O:${target.layer}`;
    case 'F': return `F:${target.layer}:${target.frame}`;
  }
}

// --- Resolution across scopes ---------------------------------------------------
// The narrowest scope wins per-DIAL (not per-effect): setting FX-12's amount on
// frame 4 must not silently reset its seeds to the registry default. Order:
// preset projection → C → N → O → F, each layered param-by-param.

function mergeSetting(base: FxSetting | undefined, over: FxSetting | undefined): FxSetting | undefined {
  if (!base) return over;
  if (!over) return base;
  return {
    enabled: over.enabled ?? base.enabled,
    params: { ...(base.params ?? {}), ...(over.params ?? {}) },
  };
}

function mergeMaps(base: FxSettingMap, over: FxSettingMap | undefined): FxSettingMap {
  if (!over) return base;
  const out: FxSettingMap = { ...base };
  for (const [id, setting] of Object.entries(over)) {
    const spec = fxSpec(id);
    out[spec.id] = mergeSetting(out[spec.id], setting)!;
  }
  return out;
}

// The recipe shape this module needs — declared structurally so the resolver
// stays usable from a host that carries its own recipe-ish object.
type FxRecipeView = Pick<CollageRecipe, 'frame_cadence' | 'boil' | 'camera' | 'print' | 'fx'> & {
  layers: readonly RecipeLayer[];
};

function findLayer(recipe: FxRecipeView, id: string): RecipeLayer {
  // Scatter pieces are addressed by their emitted id ("specks@3"); the settings
  // live on the declaring layer, so strip the particle index.
  const layerId = id.includes('@') ? id.slice(0, id.indexOf('@')) : id;
  const layer = recipe.layers.find((l) => l.id === layerId);
  if (!layer) {
    throw new Error(`collage-fx: no layer "${layerId}" in recipe (known: ${recipe.layers.map((l) => l.id).join(', ')})`);
  }
  return layer;
}

// Every effect addressable at `target`, fully resolved, in stack order.
export function resolveFxStack(
  recipe: FxRecipeView, skin: CollageSkin, target: FxTarget,
): FxStackEntry[] {
  // Presets first: the shipped 158.4 resolution is the floor everything layers on.
  let merged = presetFxSettings(recipe as CollageRecipe, skin);
  merged = mergeMaps(merged, recipe.fx);

  if (target.scope !== 'C') {
    const layer = findLayer(recipe, target.layer);
    if (target.scope === 'N') {
      merged = mergeMaps(merged, layer.fx_nested);
    } else {
      // O and F both sit under the object's own settings (an object's frame
      // inherits the object). Layer presets come before explicit fx.
      merged = mergeMaps(merged, presetLayerFxSettings(layer));
      merged = mergeMaps(merged, layer.fx);
      if (target.scope === 'F') {
        merged = mergeMaps(merged, layer.fx_frames?.[target.frame]);
      }
    }
  }

  return fxAtScope(target.scope)
    .map((spec) => fxStackEntry(spec.id, merged[spec.id]))
    .sort((a, b) => a.spec.order - b.spec.order);
}

// One effect's resolved params at one scope — the read half of the dial-surface
// API (158.066). Throws if the effect is not addressable at that scope.
export function getFxParams(
  recipe: FxRecipeView, skin: CollageSkin, target: FxTarget, idOrCode: string,
): FxStackEntry {
  const spec = fxSpec(idOrCode);
  if (!spec.scopes.includes(target.scope)) {
    throw new Error(
      `collage-fx: ${spec.code} (${spec.name}) is not addressable at scope ${target.scope} — its scopes are ${spec.scopes.join(' ')}`,
    );
  }
  const entry = resolveFxStack(recipe, skin, target).find((e) => e.spec.id === spec.id);
  if (!entry) throw new Error(`collage-fx: ${spec.code} missing from the ${target.scope}-scope stack`);
  return entry;
}

// The write half: return a NEW recipe with `params` set on `idOrCode` at
// `target`. Pure — the caller decides whether to persist. Validates every dial
// against the registry, so a bad write throws instead of landing a value the
// renderer will later ignore.
export function setFxParams(
  recipe: CollageRecipe, target: FxTarget, idOrCode: string,
  params: Record<string, unknown>, enabled?: boolean,
): CollageRecipe {
  const spec = fxSpec(idOrCode);
  if (!spec.scopes.includes(target.scope)) {
    throw new Error(
      `collage-fx: ${spec.code} (${spec.name}) is not addressable at scope ${target.scope} — its scopes are ${spec.scopes.join(' ')}`,
    );
  }
  const validated = validateFxParams(spec.id, params);

  const apply = (map: FxSettingMap | undefined): FxSettingMap => {
    const prev = map?.[spec.id];
    return {
      ...(map ?? {}),
      [spec.id]: {
        ...(enabled === undefined ? (prev?.enabled === undefined ? {} : { enabled: prev.enabled }) : { enabled }),
        params: { ...(prev?.params ?? {}), ...validated },
      },
    };
  };

  if (target.scope === 'C') return { ...recipe, fx: apply(recipe.fx) };

  const layerId = target.layer.includes('@') ? target.layer.slice(0, target.layer.indexOf('@')) : target.layer;
  let found = false;
  const layers = recipe.layers.map((l) => {
    if (l.id !== layerId) return l;
    found = true;
    switch (target.scope) {
      case 'N': return { ...l, fx_nested: apply(l.fx_nested) };
      case 'O': return { ...l, fx: apply(l.fx) };
      case 'F': return {
        ...l,
        fx_frames: { ...(l.fx_frames ?? {}), [target.frame]: apply(l.fx_frames?.[target.frame]) },
      };
    }
  });
  if (!found) {
    throw new Error(`collage-fx: no layer "${layerId}" in recipe (known: ${recipe.layers.map((l) => l.id).join(', ')})`);
  }
  return { ...recipe, layers };
}

export function parseFxTarget(key: string): FxTarget {
  const [scope, layer, frame] = key.split(':');
  switch (scope) {
    case 'C':
      return { scope: 'C' };
    case 'N':
      if (!layer) throw new Error('collage-fx: N-scope target needs a layer id (N:<layer>)');
      return { scope: 'N', layer };
    case 'O':
      if (!layer) throw new Error('collage-fx: O-scope target needs a layer id (O:<layer>)');
      return { scope: 'O', layer };
    case 'F': {
      if (!layer || frame === undefined) {
        throw new Error('collage-fx: F-scope target needs a layer id and frame (F:<layer>:<frame>)');
      }
      const f = Number(frame);
      if (!Number.isInteger(f) || f < 0) throw new Error(`collage-fx: F-scope frame "${frame}" must be a non-negative integer`);
      return { scope: 'F', layer, frame: f };
    }
    default:
      throw new Error(`collage-fx: unknown scope "${scope}" — one of ${FX_SCOPES.join(', ')}`);
  }
}
